import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { Link } from "react-router-dom";
import { hackathon, registrationState, isCancelled } from "../../data/hackathon";
import { EASE } from "./hk";
import RegistrationFlow from "./RegistrationFlow";
import "./RegistrationProvider.scss";

const { registration: reg } = hackathon;

// `available` is false outside a provider, so CTAs rendered elsewhere on the
// site fall back to the standalone /hackathon/register page instead of a
// button that would do nothing.
const Ctx = createContext({ open: () => {}, close: () => {}, isOpen: false, available: false });

export const useRegistration = () => useContext(Ctx);

/**
 * The one register CTA. Inside the provider it opens the dialog; anywhere else
 * on the site it stays an ordinary link to the registration page.
 */
export function RegisterCta({ className = "", children, onClick, ...rest }) {
  const { open, available } = useRegistration();

  if (!available) {
    return <Link to={reg.path} className={className} onClick={onClick} {...rest}>{children}</Link>;
  }
  return (
    <button
      type="button"
      className={className}
      onClick={(e) => { onClick?.(e); open(); }}
      {...rest}
    >
      {children}
    </button>
  );
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function Notice({ state }) {
  const upcoming = state === "upcoming";
  if (isCancelled()) {
    const c = hackathon.cancellation;
    return (
      <div className="rp-notice">
        <h2>{c.title}</h2>
        <p>{c.refundShort} {c.help} {hackathon.contact.email} · {hackathon.contact.phone}</p>
      </div>
    );
  }
  return (
    <div className="rp-notice">
      <h2>{upcoming ? `Registrations open on ${reg.opensLabel}` : "Registrations are closed"}</h2>
      <p>
        {upcoming
          ? `The form opens on ${reg.opensLabel}. Until then you can read about the hackathon, the tracks and the prizes.`
          : `Registrations for ${hackathon.eventName} 2026 have closed. Thank you for your interest.`}
      </p>
    </div>
  );
}

export default function RegistrationProvider({ children }) {
  const [isOpen, setOpen] = useState(false);
  const dialog = useRef(null);
  const returnTo = useRef(null);

  const open = useCallback(() => {
    returnTo.current = document.activeElement;
    setOpen(true);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    // Hand focus back to whichever CTA opened the dialog.
    returnTo.current?.focus?.();
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";

    const onKey = (e) => {
      if (e.key === "Escape") { close(); return; }
      if (e.key !== "Tab") return;

      const items = dialog.current?.querySelectorAll(FOCUSABLE);
      if (!items?.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };

    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
    };
  }, [isOpen, close]);

  const state = registrationState();

  return (
    <Ctx.Provider value={{ open, close, isOpen, available: true }}>
      {children}

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="rp-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
          >
            <motion.div
              className="rp-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="rp-title"
              ref={dialog}
              initial={{ opacity: 0, y: 18, scale: 0.985 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.99 }}
              transition={{ duration: 0.26, ease: EASE }}
            >
              <header className="rp-head">
                <div>
                  <p className="rp-head__kicker">{hackathon.eventName} 2026</p>
                  <h1 className="rp-head__title" id="rp-title">Registration</h1>
                </div>
                <button type="button" className="rp-close" onClick={close} aria-label="Close registration">
                  <X size={18} strokeWidth={2.4} />
                </button>
              </header>

              {state === "open" ? (
                <RegistrationFlow onClose={close} />
              ) : (
                <>
                  <Notice state={state} />
                  <div className="rp-notice__foot">
                    <Link to="/hackathon" className="rf-btn rf-btn--secondary" onClick={close}>
                      Read about the hackathon
                    </Link>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Ctx.Provider>
  );
}
